import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/shared/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        // Tournament Statuses
        success:
          "border-transparent bg-green-500/20 text-green-700 dark:text-green-400",
        live: "border-transparent bg-red-500/20 text-red-700 dark:text-red-400",
        warning:
          "border-transparent bg-yellow-500/20 text-yellow-700 dark:text-yellow-400",
        info: "border-transparent bg-blue-500/20 text-blue-700 dark:text-blue-400",
        completed:
          "border-transparent bg-gray-500/20 text-gray-700 dark:text-gray-400",

        // LoL Roles
        "role-top":
          "border-transparent bg-purple-500/20 text-purple-700 dark:text-purple-400",
        "role-jungle":
          "border-transparent bg-green-500/20 text-green-700 dark:text-green-400",
        "role-mid":
          "border-transparent bg-red-500/20 text-red-700 dark:text-red-400",
        "role-adc":
          "border-transparent bg-yellow-500/20 text-yellow-700 dark:text-yellow-400",
        "role-support":
          "border-transparent bg-blue-500/20 text-blue-700 dark:text-blue-400",
        "role-fill":
          "border-transparent bg-slate-500/20 text-slate-700 dark:text-slate-400",

        // Platform/Org Roles
        "role-admin":
          "border-transparent bg-red-500/20 text-red-700 dark:text-red-400",
        "role-moderator":
          "border-transparent bg-orange-500/20 text-orange-700 dark:text-orange-400",
        "role-developer":
          "border-transparent bg-indigo-500/20 text-indigo-700 dark:text-indigo-400",
        "role-streamer":
          "border-transparent bg-purple-500/20 text-purple-700 dark:text-purple-400",
        "role-support-platform":
          "border-transparent bg-cyan-500/20 text-cyan-700 dark:text-cyan-400",
        "role-user":
          "border-transparent bg-gray-500/20 text-gray-700 dark:text-gray-400",
        "role-organization":
          "border-transparent bg-emerald-500/20 text-emerald-700 dark:text-emerald-400",
        "role-captain":
          "border-transparent bg-amber-500/20 text-amber-700 dark:text-amber-400",
        "role-coach":
          "border-transparent bg-teal-500/20 text-teal-700 dark:text-teal-400",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span";

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
